import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Activity, Globe, Clock } from 'lucide-react';
function AnimatedCounter({
  end,
  suffix = '',
  duration = 2
}: {
  end: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);
      if (progress < 1) {
        setCount(Math.floor(end * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);
  return <span data-id="element-115">
      {count}
      {suffix}
    </span>;
}
export function Hero() {
  return <section className="relative bg-brand-navy min-h-[90vh] flex items-center pt-40 pb-16 overflow-hidden" data-id="element-116">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10" data-id="element-117">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-118"></div>
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-119"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-120">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-121">
          {/* Main Content */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="lg:col-span-6 text-white" data-id="element-122">
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6" data-id="element-123">
              <span className="w-2 h-2 rounded-full bg-brand-gold animate-pulse" data-id="element-124"></span>
              <span className="text-sm font-medium tracking-wide text-brand-paper" data-id="element-125">
                National Managed Care Provider
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] mb-6 text-white text-balance" data-id="element-126">
              Smarter managed care for{' '}
              <span className="text-brand-gold italic" data-id="element-127">
                workers' compensation.
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-white/80 mb-10 max-w-xl leading-relaxed font-light" data-id="element-128">
              Arissa Cost Solutions partners with carriers, TPAs, and employers
              to evolve, grow, and align medical management — driving better
              outcomes for injured workers and measurable savings for you.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-129">
              <a href="#contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-base hover:bg-white transition-colors shadow-[0_0_20px_rgba(201,162,75,0.3)] flex items-center w-full sm:w-auto justify-center" data-id="element-130">
                Request a Consultation
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-131" />
              </a>
              <a href="#services" className="text-white border border-white/30 px-8 py-4 rounded font-semibold text-base hover:bg-white/10 transition-colors w-full sm:w-auto text-center" data-id="element-132">
                Explore Services
              </a>
            </div>
          </motion.div>

          {/* Image & Floating Stats Card */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          x: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="lg:col-span-6 relative mt-10 lg:mt-0" data-id="element-133">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-auto lg:h-[600px]" data-id="element-134">
              <img src="https://images.unsplash.com/photo-1511895426328-dc8714191300?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Family embracing, representing the human side of workers compensation" className="w-full h-full object-cover" data-id="element-135" />
              <div className="absolute inset-0 bg-brand-navy/20 mix-blend-multiply" data-id="element-136"></div>
            </div>

            {/* Floating Stats Card */}
            <div className="lg:absolute lg:-bottom-10 lg:-left-12 bg-white/10 backdrop-blur-md border border-white/20 p-6 sm:p-8 rounded-2xl shadow-2xl relative overflow-hidden mt-6 lg:mt-0 lg:max-w-sm w-full" data-id="element-137">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/20 rounded-full blur-3xl -mr-10 -mt-10" data-id="element-138"></div>

              <h3 className="text-xl font-serif text-white mb-6 border-b border-white/10 pb-4" data-id="element-139">
                Proven Impact
              </h3>

              <div className="space-y-6" data-id="element-140">
                <div className="flex items-start space-x-4" data-id="element-141">
                  <div className="bg-brand-gold/20 p-2.5 rounded-lg text-brand-gold mt-1" data-id="element-142">
                    <Activity className="w-5 h-5" data-id="element-143" />
                  </div>
                  <div data-id="element-144">
                    <div className="text-2xl font-bold text-white mb-0.5" data-id="element-145">
                      <AnimatedCounter end={32} suffix="%" data-id="element-146" />
                    </div>
                    <div className="text-sm text-white/70 font-medium" data-id="element-147">
                      Average claim savings
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-id="element-148">
                  <div className="bg-brand-gold/20 p-2.5 rounded-lg text-brand-gold mt-1" data-id="element-149">
                    <Globe className="w-5 h-5" data-id="element-150" />
                  </div>
                  <div data-id="element-151">
                    <div className="text-2xl font-bold text-white mb-0.5" data-id="element-152">
                      <AnimatedCounter end={50} suffix="-State" data-id="element-153" />
                    </div>
                    <div className="text-sm text-white/70 font-medium" data-id="element-154">
                      Provider network reach
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-id="element-155">
                  <div className="bg-brand-gold/20 p-2.5 rounded-lg text-brand-gold mt-1" data-id="element-156">
                    <Clock className="w-5 h-5" data-id="element-157" />
                  </div>
                  <div data-id="element-158">
                    <div className="text-2xl font-bold text-white mb-0.5" data-id="element-159">
                      <AnimatedCounter end={24} suffix="hr" data-id="element-160" />
                    </div>
                    <div className="text-sm text-white/70 font-medium" data-id="element-161">
                      Guaranteed response time
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}